import { useCallback, useEffect, useState, type KeyboardEvent } from "react";
import {
  trackBookLinkClicked,
  trackBookRecommendationShown,
  type BookPlacement,
} from "../lib/analytics";
import {
  dismissBookNudge,
  isBookNudgeSuppressed,
  markBookNudgeClicked,
  readBookNudgeState,
  shouldShowMilestoneNudge,
} from "../lib/bookNudge";
import { handleLinkClick } from "../lib/router";
import { useSessionSummary } from "../state/useSessionSummary";

/**
 * The book recommendation: two lines of text with a link to /learn and an
 * outbound affiliate link, shown at exactly two moments (lib/bookNudge.ts
 * owns when). Everything about it is deliberately weak: it is inline text,
 * not an overlay; it never takes focus; it never covers the chart; and it
 * carries a close control plus Escape-while-focused so a reader can be rid
 * of it in one gesture. A dismissal silences it for two weeks, a click on
 * its link for good.
 *
 * The affiliate disclosure travels with the link rather than only living on
 * the learn page, because a paid link has to say so where it appears. It is
 * tiny by design, not hidden: it is also the link's `title`, so it is
 * readable on hover as well as on the page.
 */

/** The one sentence that changes per placement. */
const LINE: Record<BookPlacement, string> = {
  milestone: "Ten rounds in. The book behind candlealpha covers how to tell a pattern from noise.",
  challenge_result:
    "The book behind candlealpha covers how to tell a pattern from noise.",
  // Never rendered by this component; the learn page is the recommendation.
  learn_page: "",
};

/**
 * The disclosure, in two lengths. The short one is always on screen next to
 * the link, because a paid link has to be marked where it appears; the full
 * sentence is the `title` of both, so it is one hover or long-press away and
 * is what a screen reader announces. Two lines of nudge on a phone would
 * otherwise become four, above the chart.
 */
const DISCLOSURE = "As an Amazon Associate I earn from qualifying purchases.";
const DISCLOSURE_SHORT = "Affiliate link";

/**
 * The outbound link's `src` says which placement it was clicked from. The
 * redirect function validates it and throws it away (functions/go/book.ts):
 * it changes nothing about where the reader lands, so the same URL works
 * whether or not anyone is reading the parameter.
 */
function bookHref(placement: BookPlacement): string {
  return `/go/book?src=${placement}`;
}

/** The recommendation's body, once something has decided it should render. */
function NudgeBody({
  placement,
  onClose,
  onClicked,
}: {
  placement: BookPlacement;
  onClose: () => void;
  onClicked: () => void;
}) {
  // One impression per placement per page load; the module-level guard in
  // analytics.ts makes a remount idempotent, so this effect can stay simple.
  useEffect(() => {
    trackBookRecommendationShown({ placement });
  }, [placement]);

  // Only the /go/book link counts as an outbound book click; "Learn more" is
  // internal navigation and would inflate the number if it counted too.
  const onAmazonClick = () => {
    trackBookLinkClicked({ placement });
    onClicked();
  };

  // Escape closes it only while focus is inside: the key belongs to whatever
  // the player is actually working with, and this is never that until they
  // tab into it.
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "Escape") return;
    event.stopPropagation();
    onClose();
  };

  return (
    <div
      onKeyDown={onKeyDown}
      data-testid="book-nudge"
      aria-label="book recommendation"
      className="flex w-full flex-wrap items-start gap-x-3 gap-y-1 border-t border-muted/10 py-2 text-xs text-muted"
    >
      <p className="min-w-0 flex-1">
        {LINE[placement]}{" "}
        <a
          href="/learn"
          onClick={handleLinkClick("/learn")}
          className="text-accent hover:underline"
        >
          Learn more
        </a>
        {" · "}
        <a
          href={bookHref(placement)}
          onClick={onAmazonClick}
          target="_blank"
          rel="sponsored noopener noreferrer"
          title={DISCLOSURE}
          className="text-accent hover:underline"
        >
          View on Amazon ↗
        </a>
        <span className="ml-2 whitespace-nowrap text-[10px] text-dim" title={DISCLOSURE}>
          ({DISCLOSURE_SHORT})
        </span>
      </p>
      <button
        type="button"
        onClick={onClose}
        aria-label="dismiss book recommendation"
        className="flex min-h-8 shrink-0 items-center px-1 text-dim transition-colors hover:text-accent sm:min-h-6"
      >
        ✕
      </button>
    </div>
  );
}

/**
 * The session-milestone placement, rendered in the history/session panel
 * area. Reads the sitting's round count from the same `useSessionSummary`
 * seam the session strip prints, so the two can never disagree about what
 * "ten rounds" means.
 */
export function MilestoneBookNudge() {
  const summary = useSessionSummary();
  const [state, setState] = useState(() => readBookNudgeState());
  const close = useCallback(() => setState(dismissBookNudge()), []);
  const clicked = useCallback(() => setState(markBookNudgeClicked()), []);

  if (!shouldShowMilestoneNudge(summary.rounds, state, Date.now())) return null;
  // `basis-full` is how every other panel takes its own line in the utility
  // row's wrapping flex container (SettingsBar), and it is on the nudge
  // itself rather than on an always-present wrapper so nothing claims a line
  // while the nudge is not showing.
  return (
    <div className="basis-full">
      <NudgeBody placement="milestone" onClose={close} onClicked={clicked} />
    </div>
  );
}

/**
 * The Daily challenge result placement, under the run's summary and its
 * share controls. The result screen is already a pause in play, so this one
 * has no depth condition of its own: only the shared dismissal rules.
 */
export function ChallengeResultBookNudge() {
  const [state, setState] = useState(() => readBookNudgeState());
  const close = useCallback(() => setState(dismissBookNudge()), []);
  const clicked = useCallback(() => setState(markBookNudgeClicked()), []);

  if (isBookNudgeSuppressed(state, Date.now())) return null;
  return (
    <div className="w-full max-w-md text-left">
      <NudgeBody placement="challenge_result" onClose={close} onClicked={clicked} />
    </div>
  );
}
